import { ins } from 'src/api/axios'
import type {
    CreateStationCleanupJobsResponse,
    UpdateStationCleanupJobsResponse,
    DeleteStationCleanupJobsResponse,
    GetStationCleanupJobsByZoneIdResponse,
    BulkStationCleanupJobsResult,
    StationCleanupJob,
    StationCleanupJobWithStation,
    GetStationsByIdsResponse,
    Station,
} from 'src/api/fm/stationCleanupJob/stationCleanupJob.model'
import { getHeaders } from 'src/api/headers'
import { getError } from 'src/api/utils'

/**
 * Related {@link https://warf.voiapp.io/catalog/fm/api/fm-api/definition#/station%20cleanup%20job/createStationCleanupJobs API documentation}
 */
export const createStationCleanupJobs = async (
    stationIds: string[],
    stationCleanupJob: StationCleanupJob,
): Promise<BulkStationCleanupJobsResult | Error> => {
    const url = '/v1/fm/station-cleanup-job'
    const headers = await getHeaders()
    const data = { stationIds, stationCleanupJob }

    return ins
        .post(url, data, { headers })
        .then(res => mapCreateStationCleanupJobs(res.data))
        .catch(e => getError(e, 'response.data.errors[0]'))
}

/**
 * Related {@link https://warf.voiapp.io/catalog/fm/api/fm-api/definition#/station%20cleanup%20job/updateStationCleanupJobs API documentation}
 */
export const updateStationCleanupJobs = async (
    ids: string[],
    stationCleanupJob: StationCleanupJob,
): Promise<BulkStationCleanupJobsResult | Error> => {
    const url = '/v1/fm/station-cleanup-job'
    const headers = await getHeaders()
    const data = { ids, stationCleanupJob }

    return ins
        .put(url, data, { headers })
        .then(res => mapUpdateStationCleanupJobs(res.data))
        .catch(e => getError(e, 'response.data.errors[0]'))
}

/**
 * Related {@link https://warf.voiapp.io/catalog/fm/api/fm-api/definition#/station%20cleanup%20job/deleteStationCleanupJobs API documentation}
 */
export const deleteStationCleanupJobs = async (ids: string[]): Promise<BulkStationCleanupJobsResult | Error> => {
    const url = '/v1/fm/station-cleanup-job'
    const headers = await getHeaders()
    const data = { ids }

    return ins
        .delete(url, { data, headers })
        .then(res => mapDeleteStationCleanupJobs(res.data))
        .catch(e => getError(e, 'response.data.errors[0]'))
}

/**
 * Related {@link https://warf.voiapp.io/catalog/fm/api/fm-api/definition#/station%20cleanup%20job/getStationCleanupJobsByZoneId API documentation}
 */
export const getStationCleanupJobsByZoneId = async (
    zoneId: string,
): Promise<StationCleanupJobWithStation[] | Error> => {
    const url = `/v1/fm/station-cleanup-job/zone/${zoneId}`
    const headers = await getHeaders()

    return ins
        .get(url, { headers })
        .then(res => mapGetStationCleanupJobsByZoneId(res.data))
        .catch(e => getError(e, 'response.data.errors[0]'))
}

/**
 * Related {@link https://warf.voiapp.io/catalog/fm/api/fm-api/definition#/station%20cleanup%20job/getStationsByIds API documentation}
 */
export const getStationsByIds = async (zoneId: string, stationIds: string[]): Promise<Station[] | Error> => {
    const url = `/v1/fm/station-cleanup-job/zone/${zoneId}/stations`
    const headers = await getHeaders()

    return ins
        .get(url, { params: { ids: stationIds.join(',') }, headers })
        .then(res => mapGetStationsByIds(res.data))
        .catch(e => getError(e, 'response.data.errors[0]'))
}

const mapCreateStationCleanupJobs = (response: CreateStationCleanupJobsResponse): BulkStationCleanupJobsResult => {
    return {
        successfulCount: response.successful.length,
        failedCount: response.failed.length,
    }
}

const mapUpdateStationCleanupJobs = (response: UpdateStationCleanupJobsResponse): BulkStationCleanupJobsResult => {
    return {
        successfulCount: response.successful.length,
        failedCount: response.failed.length,
    }
}

const mapDeleteStationCleanupJobs = (response: DeleteStationCleanupJobsResponse): BulkStationCleanupJobsResult => {
    return {
        successfulCount: response.successful.length,
        failedCount: response.failed.length,
    }
}

const mapGetStationCleanupJobsByZoneId = (
    response: GetStationCleanupJobsByZoneIdResponse,
): StationCleanupJobWithStation[] => {
    return response.stationCleanupJobs.map(item => ({
        id: item.id,
        zoneId: item.zoneId,
        stationId: item.stationId,
        status: item.status,
        priority: item.priority,
        station: item.station,
    }))
}

const mapGetStationsByIds = (response: GetStationsByIdsResponse): Station[] => {
    return response.stations.map(item => ({
        id: item.id,
        name: item.name,
        displayCapacity: item.displayCapacity,
        maxCapacity: item.maxCapacity,
        occupancy: item.occupancy,
    }))
}
